import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import {
  elementOutlinePolygon,
  isFreeDrawElement,
  isLinearElement,
} from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { supportsCustomPath } from "./presets";
import { getElementAnimations, withElementAnimations } from "./types";

/** A clip awaiting a canvas drawing (motion path or morph target):
 * element id + clip index. */
export type PathDrawTarget = { elementId: string; index: number };

const isUsablePath = (el: ExcalidrawElement) =>
  (isLinearElement(el) || isFreeDrawElement(el)) &&
  !el.isDeleted &&
  el.points.length >= 2;

/** Writes `pathElementId` onto each target clip, re-selects the animated
 * elements and captures ONE undo entry covering the drawn path + binding. */
export const bindPathToClips = (
  api: Pick<
    ExcalidrawImperativeAPI,
    | "getSceneElements"
    | "getSceneElementsIncludingDeleted"
    | "mutateElement"
    | "updateScene"
  >,
  targets: readonly PathDrawTarget[],
  pathId: string,
) => {
  const byId = new Map(api.getSceneElements().map((el) => [el.id, el]));
  const selectedElementIds: Record<string, true> = {};
  for (const { elementId, index } of targets) {
    const el = byId.get(elementId);
    if (!el) {
      continue;
    }
    const clips = getElementAnimations(el);
    // the clip may have been removed or switched to another preset while the
    // user was drawing — only followPath clips consume a motion path
    if (clips[index]?.preset !== "followPath") {
      continue;
    }
    const next = clips.map((clip, i) =>
      i === index ? { ...clip, pathElementId: pathId } : clip,
    );
    api.mutateElement(el, { customData: withElementAnimations(el, next) });
    selectedElementIds[elementId] = true;
  }
  api.updateScene({
    elements: api.getSceneElementsIncludingDeleted(),
    appState: { selectedElementIds },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  return Object.keys(selectedElementIds).length;
};

/** Bakes a drawn shape's outline into `customPath` on each target morph clip
 * (the exact SVG-path format the morph pipeline already samples), then
 * removes the drawing — it was an input gesture, not scene content. One undo
 * entry covers the removal + binding. */
export const bindMorphTargetToClips = (
  api: Pick<
    ExcalidrawImperativeAPI,
    | "getSceneElements"
    | "getSceneElementsIncludingDeleted"
    | "mutateElement"
    | "updateScene"
  >,
  targets: readonly PathDrawTarget[],
  shape: ExcalidrawElement,
) => {
  // normalized 0..1 outline → "M… L… Z" on a 0..100 grid; svgPathToOutline
  // re-normalizes on read, so the scale only affects string length
  const d = `M${elementOutlinePolygon(shape)
    .map(([x, y]) => `${Math.round(x * 100)} ${Math.round(y * 100)}`)
    .join(" L")} Z`;
  const byId = new Map(api.getSceneElements().map((el) => [el.id, el]));
  const selectedElementIds: Record<string, true> = {};
  for (const { elementId, index } of targets) {
    const el = byId.get(elementId);
    if (!el) {
      continue;
    }
    const clips = getElementAnimations(el);
    // the clip may have been removed or switched to a non-morph preset while
    // the user was drawing
    if (!clips[index] || !supportsCustomPath(clips[index].preset)) {
      continue;
    }
    const next = clips.map((clip, i) =>
      i === index ? { ...clip, customPath: d } : clip,
    );
    api.mutateElement(el, { customData: withElementAnimations(el, next) });
    selectedElementIds[elementId] = true;
  }
  api.mutateElement(shape, { isDeleted: true });
  api.updateScene({
    elements: api.getSceneElementsIncludingDeleted(),
    appState: { selectedElementIds },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  return Object.keys(selectedElementIds).length;
};

// ponytail: module-level singleton — only one draw-path session can be
// active at a time, which is also the correct UX
let cancelActive: (() => void) | null = null;

/**
 * "Draw on canvas" mode: switches to a drawing tool, waits for the user to
 * finish drawing, then binds the new element to the target clips — as the
 * followPath motion path ("path" mode, line tool) or baked into customPath as
 * the morph target ("morphTarget" mode, freedraw tool) — and restores the
 * selection. Esc / switching tools without drawing cancels. Starting a new
 * session cancels a pending one.
 */
export const beginDrawPath = (
  api: ExcalidrawImperativeAPI,
  targets: readonly PathDrawTarget[],
  mode: "path" | "morphTarget" = "path",
) => {
  cancelActive?.();
  if (targets.length === 0) {
    return;
  }
  const forMorph = mode === "morphTarget";
  // ponytail: morph targets are freedraw-only; accept shape tools (rect,
  // ellipse) too if freehand proves too crude
  const tool = forMorph ? "freedraw" : "line";
  const existingIds = new Set(
    api.getSceneElementsIncludingDeleted().map((el) => el.id),
  );
  api.setActiveTool({ type: tool });
  api.setToast({
    message: forMorph
      ? "Draw the morph target shape — release to finish, Esc to cancel"
      : "Draw the motion path — double-click or Enter to finish, Esc to cancel",
    duration: 10000,
  });

  let done = false;
  // onChange can deliver a state snapshot queued BEFORE setActiveTool above
  // commits — only treat "tool left" as a cancel once the drawing tool has
  // actually been seen active
  let sawTool = false;
  const finish = () => {
    if (done) {
      return;
    }
    done = true;
    cancelActive = null;
    unsubscribe();
  };
  const unsubscribe = api.onChange(
    (elements: readonly ExcalidrawElement[], appState: AppState) => {
      if (done) {
        return;
      }
      sawTool ||= appState.activeTool.type === tool;
      // a path is still being drawn / point-edited: drag-drawing tracks
      // newElement, click-click multi-point drawing tracks multiElement
      if (
        appState.newElement ||
        appState.multiElement ||
        appState.selectedLinearElement?.isEditing
      ) {
        return;
      }
      const path = elements.find(
        (el) => !existingIds.has(el.id) && isUsablePath(el),
      );
      if (path) {
        // finish BEFORE mutating — bind triggers nested onChange calls
        finish();
        const bound = forMorph
          ? bindMorphTargetToClips(api, targets, path)
          : bindPathToClips(api, targets, path.id);
        api.setToast(
          bound > 0
            ? {
                message: forMorph ? "Morph target set" : "Motion path linked",
                duration: 2000,
              }
            : null,
        );
        return;
      }
      // tool left without producing a drawing → user cancelled
      if (sawTool && appState.activeTool.type !== tool) {
        finish();
        api.setToast(null);
      }
    },
  );
  cancelActive = finish;
};
